import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import OverflowDrawer from "@/app/shared/OverflowDrawer";
import { NEW_DAYNIGHT } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputNumber from "@/design/forms/InputNumber";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { number, string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viToggleOn } from "@sippy-platform/valkyrie";

import { Container, Grid, IconButton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import dayNightReducer from "./dayNightReducer";

function DayNight() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);

  const {
    value: dayNight,
    dispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_DAYNIGHT,
    reducer: dayNightReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 80], { message: "Name must be at most 80 characters." }),
      exten: number()
        .required({ message: "Number is required." })
        .outside([100, 119], {
          message: "Number cannot be between 100 and 119.",
        })
        .not(0, { message: "Number cannot be 0." })
        .not(1711, { message: "Number cannot be 1711." })
        .not(1712, { message: "Number cannot be 1712." })
        .not(1722, { message: "Number cannot be 1722." })
        .not(1733, { message: "Number cannot be 1733." })
        .not(1813, { message: "Number cannot be 1813." }),
    }),
    onSubmit: () => Api.storeDayNight(dayNight),
    onSuccess: (response) => {
      if (!dayNight.id) {
        dispatch({ type: "id", value: response.data.switch.id });
        navigate(`../${response.data.switch.id}`);
      }

      Promise.resolve(Api.getDayNights());
    },
  });

  // Get general data
  useEffect(() => {
    let ignore = false;

    if (Number(id) === dayNight.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      dispatch({ type: "set", value: NEW_DAYNIGHT });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getDayNight({ id: Number(id) }))
      .then((response) => !ignore && dispatch({ type: "set", value: response.data.switch }))
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [dayNight.id, dispatch, id]);

  // Handle changes
  const formHandler = (property, value) => {
    dispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getDayNights()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${dayNight.name || "Switch"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} />
                <Avatar text={dayNight.name} icon={viToggleOn} />
              </IconButton>
            ) : (
              <Avatar text={dayNight.name} icon={viToggleOn} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={dayNight.id || "nk"}
                isActionable={!!dayNight?.id}
                onDeleteCheck={() => Api.deleteDayNight(dayNight, false)}
                onDelete={() => Api.deleteDayNight(dayNight, true)}
                name={`${dayNight.name} (${dayNight.exten})`}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={dayNight.name || "Unnamed switch"}
          subheader={dayNight.exten || "No number set"}
        />
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText required id="name" label="Name" onChange={formHandler} value={dayNight.name} error={errors} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputNumber
              required
              id="exten"
              label="Number"
              onChange={formHandler}
              value={dayNight.exten}
              error={errors}
              helperText="Number cannot be 0 or any Belgian emergency number."
            />
          </Grid>
        </FormGroup>
        <FormGroup title="Switch options">
          <Grid size={12}>
            <InputToggle
              id="state"
              error={errors}
              onChange={formHandler}
              states={[false, true]}
              value={dayNight.state}
              primary="Use day mode"
              secondary={
                dayNight.exten ? (
                  <>
                    You can also toggle this switch by calling <strong>{dayNight.exten}*</strong>.
                  </>
                ) : (
                  "Set an extension first."
                )
              }
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <OverflowDrawer
              label="Day mode"
              value={dayNight.onOverflow}
              propertyName="onOverflow"
              overflowHandler={(value) => formHandler("onOverflow", value)}
              filter={{ exten: [dayNight.exten], custom: [], sound: [] }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <OverflowDrawer
              label="Night mode"
              value={dayNight.offOverflow}
              propertyName="offOverflow"
              overflowHandler={(value) => formHandler("offOverflow", value)}
              filter={{ exten: [dayNight.exten], custom: [], sound: [] }}
            />
          </Grid>
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default DayNight;
